// Vulnerabilities — fleet-wide open CVE backlog (the "what" behind the C/H/M/L counts).
//
// Fed by GET /operations/vulnerabilities, which returns the findings persisted from
// detailed remote-ops vuln-scans (critical/high, with fix versions). Counts-only data
// still lives on the Fleet page; this is the actionable, per-CVE triage list.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldAlert, RefreshCw, Info } from 'lucide-react';
import { apiRequest } from '@/lib/supabase';
import { EmptyState } from '@/components/shared/EmptyState';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FindingsTable, SeverityBadge } from '@/pages/RemoteOps';

interface VulnFinding {
  id: string; instance_id: string; cve: string | null; package: string | null;
  installed_version: string | null; fixed_version: string | null;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'unknown';
  title: string | null; scanner: string | null; detected_at: string;
  organization_id?: string | null; organization_name?: string | null; // owning company
}

// The authoritative severity COUNTS come from each install's latest scan summary
// (license_activations.vuln_summary), exposed via /fleet/instances. Findings itemize
// critical+high only; medium/low are counts-only — so the tiles read from here, not the
// findings rows, and never show a misleading 0.
interface VulnSummary { critical?: number; high?: number; medium?: number; low?: number; scanned_at?: string }
interface InstallRow { instance_id: string; vuln_summary?: VulnSummary | null }

const btnSec: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
  borderRadius: 8, background: 'var(--surface)', color: 'var(--text-primary)',
  border: '1px solid var(--border)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
};
const card: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow-card)',
};

function Kpi({ label, value, tint }: { label: string; value: number; tint: string }) {
  return (
    <div style={{ ...card, padding: '14px 18px', flex: 1 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: tint, marginTop: 4 }}>{value}</div>
    </div>
  );
}

export function VulnerabilitiesPage() {
  const [findings, setFindings] = useState<VulnFinding[]>([]);
  const [installs, setInstalls] = useState<InstallRow[]>([]);
  const [sev, setSev] = useState<string>('all');
  const [instance, setInstance] = useState<string>('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiRequest<VulnFinding[]>('/operations/vulnerabilities').then((f) => setFindings(f || [])).catch(() => setFindings([])),
      apiRequest<InstallRow[]>('/fleet/instances').then((r) => setInstalls(r || [])).catch(() => setInstalls([])),
    ]).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  // Instance picker = every install (so one with only medium/low — and thus no itemized
  // findings — is still selectable).
  const instances = useMemo(
    () => Array.from(new Set([...installs.map((i) => i.instance_id), ...findings.map((f) => f.instance_id)].filter(Boolean))).sort(),
    [installs, findings]
  );
  // Counts come from the scan summary (all four severities), honoring the instance filter.
  const totals = useMemo(() => {
    const t = { critical: 0, high: 0, medium: 0, low: 0 } as Record<string, number>;
    for (const inst of installs) {
      if (instance !== 'all' && inst.instance_id !== instance) continue;
      const vs = inst.vuln_summary || {};
      t.critical += Number(vs.critical || 0);
      t.high += Number(vs.high || 0);
      t.medium += Number(vs.medium || 0);
      t.low += Number(vs.low || 0);
    }
    return t;
  }, [installs, instance]);

  const visible = useMemo(
    () => findings.filter((f) => (sev === 'all' || f.severity === sev) && (instance === 'all' || f.instance_id === instance)),
    [findings, sev, instance]
  );

  // Map the DB shape (installed_version/fixed_version) to the shared FindingsTable shape.
  const rows = visible.map((f) => ({
    cve: f.cve, package: f.package, installed: f.installed_version, fixed: f.fixed_version,
    severity: f.severity, title: f.title, instance_id: f.instance_id,
    organization_id: f.organization_id, organization_name: f.organization_name,
  }));

  return (
    <div style={{ padding: '24px 32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <ShieldAlert size={22} />
          <h1 style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-.01em', margin: 0 }}>Vulnerabilities</h1>
        </div>
        <button onClick={load} style={btnSec}><RefreshCw size={13} /> Refresh</button>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <Kpi label="Critical" value={totals.critical} tint="var(--ps-pill-red-tx)" />
        <Kpi label="High" value={totals.high} tint="var(--ps-pill-amber-tx)" />
        <Kpi label="Medium" value={totals.medium} tint="var(--ps-pill-amber-tx)" />
        <Kpi label="Low" value={totals.low} tint="var(--ps-pill-blue-tx)" />
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 6 }}>
        <Info size={13} />
        Counts are from the latest scan. The table itemizes <strong style={{ color: 'var(--text-secondary)' }}>&nbsp;critical &amp; high&nbsp;</strong> (with fix versions); medium &amp; low are counts-only.
      </div>

      <div style={{ ...card, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 600, marginRight: 'auto' }}>Open findings</span>
          <div style={{ width: 160 }}>
            <Select value={sev} onValueChange={setSev}>
              <SelectTrigger aria-label="Severity"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All severities</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div style={{ width: 220 }}>
            <Select value={instance} onValueChange={setInstance}>
              <SelectTrigger aria-label="Instance"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All installs</SelectItem>
                {instances.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
        ) : visible.length === 0 ? (
          <EmptyState
            icon="shield-check"
            title={findings.length === 0 ? 'No vulnerabilities recorded' : 'No findings match the filter'}
            body={findings.length === 0
              ? 'Dispatch a vuln-scan from Remote Operations — its critical/high findings will appear here.'
              : 'Try a different severity or install.'}
          />
        ) : (
          <div style={{ padding: '8px 12px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 6px 10px', fontSize: 12, color: 'var(--text-muted)' }}>
              <SeverityBadge sev={sev === 'all' ? 'critical' : sev} />
              showing {visible.length} of {findings.length} findings
            </div>
            <FindingsTable findings={rows} showInstance />
          </div>
        )}
      </div>
    </div>
  );
}
